import { useCallback } from "react"
import { useAuthBearerFetch, bearerFetcher, MatsuriError } from "matsuri-hooks"
import { useAuth } from "m2m-components/storage/useM2mAuth"
import { m2mCleaning_cleanerV4 } from "@/endpoints/m2m-cleaning.cleaner-v4"
import { imageCompressToWebp } from "@/utils/image"
import { uploadBlobWithPresignedPut } from "@/utils/presignedUpload"
import { isSpareKeyNumberRegistered } from "@/model/spareKeybox"

export interface SpareKeyboxHistory {
    rotationDate: string
    keyboxNumber: string
}

export interface SpareKeyboxRotation {
    keyboxNumber: string
    currentKeyboxNumber: string
    /** 変更前の番号が分かっているか（false = 番号未登録） */
    isRegistered: boolean
    isRotated: boolean
    isAvailable: boolean
    canRotate: boolean
    daysSinceLastRotation?: number
    nextRotationDate?: string
    installationMethod: string
    location: string
    outLocation: string
    hasPhotoUrl: boolean
    rotate: (photoFile?: File) => Promise<void>
}

interface SpareKeyboxNumberResponse {
    isAvailable: boolean
    // 変更前の番号(keyboxNumber)が分かっているか。古い API では返らない
    isRegistered?: boolean
    keyboxNumber: string
    newKeyboxNumber: string
    installationMethod: string
    location: string
    outLocation: string
    isRotated: boolean
    photoUrl?: string
    // 処理頻度によって鍵番号を変更できるかどうか
    canRotate: boolean
    // 最後に鍵番号を変更してからの日数
    daysSinceLastRotation?: number
    // 次に鍵番号を変更する予定日
    nextRotationDate?: string
}

interface SpareKeyboxRotationWithLoadingTrue extends Partial<SpareKeyboxRotation> {
    isLoading: true
    error: undefined
}

interface SpareKeyboxRotationWithError extends Partial<SpareKeyboxRotation> {
    isLoading: false
    error: MatsuriError
}

interface SpareKeyboxRotationWithLoadingFalse extends SpareKeyboxRotation {
    isLoading: false
    error: undefined
}

type SpareKeyboxRotationWithLoading =
    | SpareKeyboxRotationWithLoadingTrue
    | SpareKeyboxRotationWithLoadingFalse
    | SpareKeyboxRotationWithError

export const useSpareKeyboxRotation = (
    cleaningId: string
): SpareKeyboxRotationWithLoading => {
    const { token } = useAuth()

    const {
        data: spareKeyboxNumber,
        refetch,
        isLoading,
        error
    } = useAuthBearerFetch<SpareKeyboxNumberResponse>(
        token,
        cleaningId
            ? m2mCleaning_cleanerV4.getSpareKeyboxNumber({ cleaningId })
            : null,
        {
            method: m2mCleaning_cleanerV4.getSpareKeyboxNumber.method
        }
    )

    const rotate = useCallback(
        async (photoFile?: File) => {
            if (!token || !cleaningId) return

            let uploadId: string | undefined
            if (photoFile) {
                const compressed = await imageCompressToWebp(photoFile, {
                    quality: 0.85,
                    maxWidth: 1500,
                    maxHeight: 1500
                })
                const result = await uploadBlobWithPresignedPut({
                    token,
                    issueUrl:
                        m2mCleaning_cleanerV4.issueSpareKeyboxPictureUploadUrl({
                            cleaningId
                        }),
                    blob: compressed
                })
                uploadId = result.uploadId
            }

            const completeResult = await bearerFetcher(
                token,
                m2mCleaning_cleanerV4.completeSpareKeyboxRotation({
                    cleaningId
                }),
                {
                    method: "POST",
                    body: JSON.stringify({ uploadId })
                }
            )
            if (completeResult.error) {
                throw completeResult.error
            }

            await refetch()
        },
        [token, cleaningId, refetch]
    )

    if (error) {
        return {
            error,
            isLoading: false
        }
    }

    if (isLoading) {
        return {
            error: undefined,
            isLoading: true
        }
    }

    return {
        currentKeyboxNumber: spareKeyboxNumber?.keyboxNumber || "",
        isRegistered: spareKeyboxNumber
            ? isSpareKeyNumberRegistered(spareKeyboxNumber)
            : false,
        keyboxNumber: spareKeyboxNumber?.newKeyboxNumber || "",
        isRotated: spareKeyboxNumber?.isRotated || false,
        installationMethod: spareKeyboxNumber?.installationMethod || "",
        location: spareKeyboxNumber?.location || "",
        outLocation: spareKeyboxNumber?.outLocation || "",
        isAvailable: spareKeyboxNumber?.isAvailable || false,
        canRotate: spareKeyboxNumber?.canRotate || false,
        daysSinceLastRotation: spareKeyboxNumber?.daysSinceLastRotation,
        nextRotationDate: spareKeyboxNumber?.nextRotationDate,
        hasPhotoUrl: spareKeyboxNumber?.photoUrl ? true : false,
        rotate,
        isLoading: false,
        error: undefined
    }
}

export interface LatestSpareKeyboxNumberResponse {
    /** 現在の番号が分かっているか（false = 番号未登録） */
    isRegistered?: boolean
    keyboxNumber: string
    installationMethod: string
    location: string
    outLocation: string
    histories: SpareKeyboxHistory[]
}

export const useLatestSpareKeyboxInfo = (
    cleaningId?: string
): LatestSpareKeyboxNumberResponse => {
    const { token } = useAuth()

    const endpoint = m2mCleaning_cleanerV4.getLatestSpareKeyboxNumber
    const { data: latestSpareKeyboxNumber } =
        useAuthBearerFetch<LatestSpareKeyboxNumberResponse>(
            token,
            cleaningId ? endpoint({ cleaningId }) : null,
            {
                method: endpoint.method
            }
        )

    return {
        // 取得前・取得失敗時は番号未登録と断定しない
        isRegistered: latestSpareKeyboxNumber
            ? isSpareKeyNumberRegistered(latestSpareKeyboxNumber)
            : true,
        keyboxNumber: latestSpareKeyboxNumber?.keyboxNumber || "-",
        installationMethod: latestSpareKeyboxNumber?.installationMethod || "-",
        location: latestSpareKeyboxNumber?.location || "-",
        outLocation: latestSpareKeyboxNumber?.outLocation || "-",
        histories: latestSpareKeyboxNumber?.histories || []
    }
}
